import hyperdom from 'hyperdom'
import scrollToElement from 'scroll-to-element'
import * as gtmDataLayer from '../../../analytics/gtm/dataLayer.js'
import {get} from 'lodash-es'
import format from 'root-core/utils/text/stringFormatFilter.js'

export default class PassengersBooking {
  constructor ({quoteCriteria, config, switchToTab, passengersDetails, booking, api, modals, showLoading, hideLoading, features, quoteEmail, user, extraStays}) {
    this.api = api
    this.booking = booking
    this.config = config
    this.features = features
    this.hideLoading = hideLoading
    this.modals = modals
    this.passengersDetails = passengersDetails
    this.quoteCriteria = quoteCriteria
    this.quoteEmail = quoteEmail
    this.showLoading = showLoading
    this.switchToTab = switchToTab
    this.user = user
    this.extraStays = extraStays
    this.showTravelProtectionWarning = true
    this.fullNamesConfirmed = false 
  }

  moveToPayment () {
    this.switchToTab('payment')
  }

  submit (bookingOptions) {
    const {additionalValidation, bookingAction, continuePromiseFactory} = bookingOptions || {}
    delete this.bookedWithExpressCheckout
    if (!this.passengersDetails.submit() | (additionalValidation && !additionalValidation().valid)) {
      return
    }

    this.childAgeError = this.passengersDetails.allChildrenOldEnoughForTourAsOfToday()
    if (this.childAgeError) {
      return
    }

    const continuePromise = continuePromiseFactory ? continuePromiseFactory() : void 0

    if (
      !continuePromise && 
      (this.quoteCriteria.hasFlights() || this.quoteCriteria.hasPromisedFlights()) &&
      !this.quoteCriteria.allPassengersHaveMiddleNames() &&
      !this.fullNamesConfirmed
    ) {
      this.showFullNameWarning = true
      this.scrollToName = false
      this.modals.stopBodyScroll()
      return
    } else {
      this.showFullNameWarning = false
    }

    if (!this.quoteCriteria.hasTravelProtection()
      && this.config.features.showTravelProtectionWarning
      && this.showTravelProtectionWarning) {

      return this.modals.show({
        render: () => this.renderTravelProtectionWarning(bookingOptions),
        close: () => {},
      })
    }

    return this.booking.book().then(async booking => {
      if (continuePromise) {
        try {
          await continuePromise
        } catch (e) {
          return
        }
      }

      if (booking.hotelsNotBooked || booking.flightsNotBooked || booking.tourOnRequest) {
        this.modals.show({
          backdropCloseDisabled: true,
          render: () => this.renderTourWarningModal({booking, bookingAction}),
        })
        gtmDataLayer.push({event: 'userBookingWarning', userBookingWarning: 'Displayed'})
      } else {
        bookingAction ? bookingAction() : this.moveToPayment()
      }
    }).catch(async (error) => {
      await this.sendQuoteEmailOnError(error)
      this.api.onError({errors: error.errorMessages || error})
    })
  }

  renderTravelProtectionWarning (bookingOptions) {
    const makeBooking = () => {
      this.showTravelProtectionWarning = false
      this.modals.close()
      this.submit(bookingOptions)
    }

    const leaveOnPassengerTab = () => {
      this.showTravelProtectionWarning = false
      this.modals.close()
      scrollToElement('.passengers-section', {
        ease: 'linear',
        duration: 300,
      })
    }

    if (this.quoteCriteria.isTravelProtectionAvailable()) {
      return <div class='travel-protection-warning-popup'>
        <div class="travel-protection-warning-popup__header">{this.config.insurance.warningPopups.header}</div>
        {this.config.insurance.warningPopups.insurancePurchaseTexts.map(text => <div class="travel-protection-warning-popup__text">{text}</div>)}
        <div class="travel-protection-warning-popup__buttons">
          <button class={`travel-protection-warning-popup__button reversed-colors`} data-test='travel-protection-warning-no-button' href='#' onclick={makeBooking} type='button'>No, thank you</button>
          <button class={`travel-protection-warning-popup__button`} data-test='travel-protection-warning-yes-button' href='#' onclick={leaveOnPassengerTab} type='button'>Yes, add</button>
        </div>
      </div>
    } else {
      const brand = this.config.insurance.externalInsuranceProviderUrl
        ? `<a href=${this.config.insurance.externalInsuranceProviderUrl} target='_blank'>${this.config.brandName}</a>`
        : `<span>${this.config.brandName}</span>`

      return <div class='travel-protection-warning-popup'>
        <div class="travel-protection-warning-popup__header">{this.config.insurance.warningPopups.header}</div>
        <div class="travel-protection-warning-popup__text">{hyperdom.rawHtml('', format(this.config.insurance.warningPopups.insuranceReminderText, brand))}</div>
        <div class="travel-protection-warning-popup__single-button">
          <button class={`travel-protection-warning-popup__button`} href='#' onclick={makeBooking} type='button'>OK, thank you</button>
        </div>
      </div>
    }
  }

  sendQuoteEmailOnError (error) {
    if (error.serverError) {
      if (this.features.shouldSendQuoteEmailOnError) {
        if (!this.errorEmailSent) {
          const recipientDetails = this.user.isTravelAgent ? {
            firstName: this.user.firstName,
            lastName: this.user.lastName,
            emailAddress: this.user.config.allowedSellingCompanies[this.config.sellingCompanyCode].communicationMethod === 'sendToAgency' ? this.user.agencyEmailAddress
              : this.user.email,
          } : this.quoteCriteria.leadPassenger
          return this.quoteEmail.send({
            email: recipientDetails.emailAddress,
            firstName: recipientDetails.firstName,
            lastName: recipientDetails.lastName,
          }, {isFailedBooking: true}).then(() => {
            this.errorEmailSent = true
            this.errorMessage = "Sorry we can't take your booking right now, we've sent you an email with\n" +
              "the details of your booking so you can try again later."
          })
        }
      }
      else {
        this.errorMessage = `Sorry we can not take your booking right now, please call us on ${this.config.phone.general}`
      }
    }
  }

  continueBookingWithoutFlights ({bookingAction} = {}) {
    gtmDataLayer.push({event: 'userBookingWarning', userBookingWarning: 'Continued to payment'}) // We want to raise this event only when it's coming from the user warnings modal.
    this.quoteCriteria.flights.clear()
    this.quoteCriteria.promisedFlights.clear()
    this.modals.close()
    return bookingAction ? bookingAction() : this.moveToPayment()
  }

  cancelBookingWarnings () {
    if (this.booking.booking.flightsNotBooked) {
      this.quoteCriteria.flights.clear()
      this.quoteCriteria.promisedFlights.clear()
    }
    if (this.booking.booking.hotelsNotBooked && this.features.removeHotelsOnRequestFromModal) {
      const hotelsBefore = this.quoteCriteria.hotelsBefore
      const hotelsAfter = this.quoteCriteria.hotelsAfter

      if (hotelsBefore.hotels.length && hotelsBefore?.selectedNights?.number) {
        this.extraStays.setAccommodation('pre', 0)
      }
      if (hotelsAfter.hotels.length && get(hotelsAfter, 'selectedNights.number')) {
        this.extraStays.setAccommodation('post', 0)
      }

      this.config.features.extrasEnabled = true
      this.switchToTab('extras')
    }
    this.booking.unload()
    this.modals.close()
  }

  requestTrip () {
    this.modals.close()
    this.showLoading()

    return this.booking.acceptTourOnRequest().then(() => {
      this.hideLoading()
      this.api.onSuccess()
      this.switchToTab('confirmation')
    }).catch((error) => {
      const phoneNumber = this.config.phone.general

      const defaultErrorMessage = [`There was an error while trying to put your tour on request. Please give us a call on ${phoneNumber}`]
      const bookingInMoreThanOneTabErrorMessage = [`Please note you are trying to book this trip in more than one tab. You can finish your booking in the newly opened one.`, `If you have any problems, please give us a call on ${phoneNumber}`]

      const errorMessage = error.statusCode === 403 ? bookingInMoreThanOneTabErrorMessage : defaultErrorMessage

      this.api.onError({
        ErrorMessages: errorMessage,
      })
    })
  }

  renderHotelsOnRequestWarning () {
    return this.features.removeHotelsOnRequestFromModal
      ? <div>
        <p>We need to check the availability for your extra hotel nights.</p>
        <p>Continue with your booking and we'll be in touch shortly with confirmation of availability.</p>
      </div>
      : <p>Your extra nights are currently on request. If you choose to continue the booking, one of the team will be in touch to confirm availability.</p>
  }

  renderTourOnRequestWarning () {
    return <div>
      {hyperdom.rawHtml('p', this.config.passengersBookingMessages.tourOnRequestWarning)}
    </div>
  }

  renderFlightsOnRequestWarning () {
    return <div>
      <p>We have been unable to book your flights today, and have removed them from your booking.</p>
      <p>If you choose to continue the booking, one of the team will be in touch to help you book new flights.</p>
    </div>
  }

  renderTourWarningHeader ({tourOnRequest, flightsNotBooked, hotelsNotBooked}) {
    if (tourOnRequest || flightsNotBooked || !this.features.removeHotelsOnRequestFromModal) {
      return this.config.passengersBookingMessages.tourOnRequestHeader
    } else if (hotelsNotBooked) {
      return 'Hotel Night Request'
    }
    return ''
  }

  renderTourWarningMessage ({tourOnRequest, flightsNotBooked, hotelsNotBooked}) {
    const messages = []

    if (!this.features.removeHotelsOnRequestFromModal || tourOnRequest || flightsNotBooked) {
      messages.push(hyperdom.rawHtml('p', this.config.passengersBookingMessages.tourWarning))
    }
    if (tourOnRequest) {
      messages.push(this.renderTourOnRequestWarning())
    }
    if (flightsNotBooked) {
      messages.push(this.renderFlightsOnRequestWarning())
    }
    if (hotelsNotBooked) {
      messages.push(this.renderHotelsOnRequestWarning())
    }
    return messages
  }

  renderCancelButton ({hotelsNotBooked}) {
    let buttonText = 'Cancel Request'
    if (hotelsNotBooked) {
      buttonText = 'Cancel Hotel Request'
    }
    return <div class='u-inbl -middle'>
      <a class='place-booking-warnings__cancel' onclick={(ev) => {this.cancelBookingWarnings(); ev.preventDefault()}}>{buttonText}</a>
    </div>
  }

  renderTourWarningModal ({booking, bookingAction} = {}) {
    return <div class='tour-quote place-booking-warnings'>
      <div>
        <div className='modal__header'>
          <h3>{this.renderTourWarningHeader(booking)}</h3>
        </div>
        {this.renderTourWarningMessage(booking)}
      </div>
      <div class='place-booking-warnings__actions'>
        {this.renderCancelButton(booking)}
        <div class='u-inbl -middle'>
          {
            booking.tourOnRequest
              ? <button class='request-trip btn-secondary' onclick={() => this.requestTrip()} type='button'>Request Trip</button>
              : <button class='continue-booking btn-secondary' onclick={() => this.continueBookingWithoutFlights({bookingAction})} type='button'>Continue with booking</button>
          }
        </div>
      </div>
    </div>
  }

  goBackAndCorrectNames () {
    this.scrollToName = true
    this.showFullNameWarning = false
    this.modals.startBodyScroll()
  }

  confirmNamesAreCorrect () {
    this.fullNamesConfirmed = true
    this.submit()
    this.modals.startBodyScroll()
  }

  renderFullNameWarning () {
    return this.showFullNameWarning
      ? <div class='o-centered-modal-wrapper'>
        <div class='o-centered-modal full-name-warning'>
          <div className='full-name-warning__mobile-header'>
            <h2 class='u-margin-top-none'>Confirm Passenger Details</h2>
            <i className='modal__exit' onclick={(ev) => {ev.preventDefault(); return this.goBackAndCorrectNames()}}></i>
          </div>
          <div class='full-name-warning__description'>
            <p>Please ensure that passenger names listed below are exactly the way they appear on their passports, including any middle name(s). Airlines may deny boarding for missing or incorrect information.</p>
            <p>Please confirm that these are the correct full names for all passengers.</p>
            <div class="full-name-warning__passengers-list">
              {
                this.quoteCriteria.passengers.map((passenger, index) => {
                  return <p class="full-name-warning__passenger-details"><span>{index + 1}</span> {passenger.firstName} {passenger.middleName} {passenger.lastName}</p>
                })
              }
            </div>
          </div>
          <hr/>
          <div class='u-margin-top-medium'>
            <div>
              <button class='btn-primary -full-width full-name-warning__accept' onclick={(e) => { e.preventDefault(); return this.confirmNamesAreCorrect() }} type='button'>Yes, this is correct</button>
            </div>
            <div>
              <a class='full-name-warning__cancel' href='#'
                onclick={(e) => { e.preventDefault(); return this.goBackAndCorrectNames() }}>
                    No, I'll correct this
              </a>
            </div>
          </div>
        </div>
      </div>
      : ''
  }
}
